<template>
  <Toast />
  <CustomerJourneyPopups />
  <Transition name="login-animation-fade">
    <div v-if="authStore.shouldShowLoginAnimation" class="dashsocial-animation-wrapper">
      <LottieAnimation v-bind="animationOptions" @on-complete="clearLoginAnimation()" />
    </div>
  </Transition>
  <PlatformOutage v-if="notificationStore.platformIsDown" />
  <PageNotFound v-else-if="!brandLabelInUrlIsValid" />
  <div
    v-else
    class="app-view"
    :class="{
      'report-view': routeMetaReport,
    }"
  >
    <!-- Loading overlay shown while fetching identity - keeps router-view mounted -->
    <div v-if="isFetchingIdentity" class="identity-loading-overlay">
      <CircularLoader />
    </div>
    <OutageBanners />
    <DashingLayout :show-nav="showNav">
      <router-view />
    </DashingLayout>

    <component
      v-bind="modal.props"
      :is="modal.component"
      v-for="(modal, index) in globalModals"
      :key="index"
    />
    <MultiSelectTopBar
      v-if="mediaSelectStore.multiSelectBarEnabled"
      :show-add-to-gallery-control="showAddToGalleryOption"
    />
    <NotificationPopups />
    <PlatformPopups />
    <ConfirmationDialog />
    <SvgDefs />
    <VisionChatPopup v-if="showVisionAi" />
    <HubSpotChat v-if="showHubspotChat" />
    <CheckTimezoneDialog v-if="showCheckTimezoneDialog" />
  </div>
</template>

<script>
import '@/styles/main.css';

import { useChartSetup } from '@dashhudson/dashing-ui/chart';
import { ConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { SvgDefs } from '@dashhudson/dashing-ui/icon';
import { preloadLottie } from '@dashhudson/dashing-ui/loaders/lottie';
import { LottieAnimation } from '@dashhudson/dashing-ui/lottie-animation';
import { useHead } from '@unhead/vue';
import isEqual from 'lodash/isEqual';
import { mapStores } from 'pinia';
import { computed, defineComponent } from 'vue';
import { useRoute } from 'vue-router';

import dashSocialIconPrimary from '@/assets/images/dashsocial/dash-social-icon-primary.svg';
import dashSocialLoadingAnimation from '@/assets/images/dashsocial/dash-social-loading-animation.json?url';
import dashSocialLogoPrimaryBlack from '@/assets/images/dashsocial/dash-social-logo-primary-black.svg';
import noAccessBg from '@/assets/images/no-access-bg.jpg';
import CheckTimezoneDialog from '@/components/CheckTimezoneDialog.vue';
import CircularLoader from '@/components/CircularLoader.vue';
import PlatformPopups from '@/components/common/connections/PlatformPopups.vue';
import DashingLayout from '@/components/dashingUI/DashingLayout.vue';
import Toast from '@/components/dashingUI/Toast.vue';
import HubSpotChat from '@/components/hubspot/HubSpotChat.vue';
import CustomerJourneyPopups from '@/components/InAppTrials/CustomerJourneyPopups.vue';
import MultiSelectTopBar from '@/components/MultiSelectTopBar.vue';
import NotificationPopups from '@/components/NotificationPopups.vue';
import OutageBanners from '@/components/OutageBanners.vue';
import PageNotFound from '@/components/PageNotFound.vue';
import PlatformOutage from '@/components/PlatformOutage.vue';
import { useVisionAiPermissions } from '@/components/VisionAi/composables/useVisionAiPermissions';
import VisionChatPopup from '@/components/VisionAi/VisionChatPopup.vue';
import { branding, sessionInterval, trackSessionKey } from '@/config';
// Use trailing underscores to avoid collisions with globals injected by these tools
import globalModalsMixin from '@/mixins/globalModalsMixin';
import { BRAND } from '@/models/auth/permissions.enum';
import { CHANNELS, filterNonCompetitiveChannels } from '@/models/dashboards/channels.enum';
import { useAuthStore } from '@/stores/auth';
import { useChiliPiperStore } from '@/stores/chili-piper';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDatadogStore } from '@/stores/datadog';
import { PROVIDERS as EMAIL_PROVIDERS } from '@/stores/email';
import { useFlagStore } from '@/stores/flag';
import { useFullstoryStore } from '@/stores/fullstory';
import { useGalleryStore } from '@/stores/gallery';
import { useHubspotStore } from '@/stores/hubspot';
import { useIdentityStore } from '@/stores/identity';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useInstagramAccountStore } from '@/stores/instagram-account';
import { AUTHENTICATION_TYPE, useIntegrationStore } from '@/stores/integration';
import { useIntercomStore } from '@/stores/intercom';
import { useMediaSelectStore } from '@/stores/media-select';
import { useMetricsStore } from '@/stores/metrics';
import { useNotificationStore } from '@/stores/notification';
import { usePlatformStore } from '@/stores/platform';
import { useSchedulerStore } from '@/stores/scheduler';
import { useSearchStore } from '@/stores/search';
import { useSettingsStore } from '@/stores/settings';
import { useSharedStore } from '@/stores/shared';
import { useSurvicateStore } from '@/stores/survicate';
import { useTokenStore } from '@/stores/token';
import { useTrackingStore } from '@/stores/tracking';
import { useTwitterStore } from '@/stores/twitter';
import { useVisionAiStore } from '@/stores/vision-ai';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';
import { loadChartjs } from '@/utils/chartjs-loader';
import { setCssPageMedia } from '@/utils/dom';
import { preloadImages } from '@/utils/preloadImages';

const comp = defineComponent({
  name: 'App',
  components: {
    VisionChatPopup,
    HubSpotChat,
    PageNotFound,
    CircularLoader,
    MultiSelectTopBar,
    NotificationPopups,
    PlatformOutage,
    PlatformPopups,
    CustomerJourneyPopups,
    ConfirmationDialog,
    Toast,
    SvgDefs,
    DashingLayout,
    CheckTimezoneDialog,
    OutageBanners,
    LottieAnimation,
  },
  mixins: [globalModalsMixin],
  setup() {
    useChartSetup(() => loadChartjs());

    const viewportScale = window?.visualViewport?.scale ?? 1;
    const route = useRoute();
    const metaContent = computed(() =>
      route?.meta?.setResponsiveViewportMetaTag
        ? 'width=device-width, initial-scale=1'
        : `width=device-width, initial-scale=${viewportScale}`,
    );
    useHead({
      meta: () => [
        {
          name: 'viewport',
          content: metaContent.value,
        },
      ],
    });

    return {
      metaContent,
    };
  },
  data() {
    return {
      currentCursorPos: [0, 0],
      savedCursorPos: [0, 0],
      branding,
      animationOptions: {
        animationLink: dashSocialLoadingAnimation,
        autoPlay: true,
        loop: false,
        width: '700px',
        height: '700px',
      },
      loginAnimationTimeout: null,
    };
  },
  computed: {
    ...mapStores(
      useAuthStore,
      useTokenStore,
      useNotificationStore,
      usePlatformStore,
      useMediaSelectStore,
      useMetricsStore,
      useSettingsStore,
      useTwitterStore,
      useIntercomStore,
      useHubspotStore,
      useChiliPiperStore,
      useSharedStore,
      useFullstoryStore,
      useDatadogStore,
      useInstagramAccountStore,
      useGalleryStore,
      useSchedulerStore,
      useTrackingStore,
      useImpersonatorStore,
      useSurvicateStore,
      useVisionAiStore,
      useIdentityStore,
      useSearchStore,
      useDashboardsStore,
      useIntegrationStore,
      useFlagStore,
    ),
    brandLabelInUrlIsValid() {
      const brandLabel = this.$route?.params?.brandLabel;
      return (
        !this.authStore.identity ||
        !brandLabel ||
        Object.keys(this.authStore.identity.brands).includes(brandLabel) ||
        Object.keys(this.authStore.identity?.inactive_brands || {}).includes(brandLabel)
      );
    },
    isFetchingIdentity() {
      return this.tokenStore.token && !this.authStore.isLoggedIn;
    },
    hasOpenPopup() {
      return (
        this.globalModals.length > 0 ||
        this.schedulerStore.popupType ||
        this.galleryStore.showShoppableSettingsPopup
      );
    },
    showAddToGalleryOption() {
      return (
        this.routeName !== 'instagram.ugc.hashtag' &&
        !this.routeName?.startsWith('scheduler') &&
        !this.mediaSelectStore.isTargetingBrands
      );
    },
    routeMetaReport() {
      return this.$route?.meta?.report;
    },
    routeMetaShared() {
      return this.$route?.meta?.shared;
    },
    routeSalesforceEmbed() {
      return this.$route?.meta?.isSalesforceEmbed;
    },
    routeMetaHideNav() {
      return this.$route?.meta?.hideNav;
    },
    routeMetaHideVisionAi() {
      return this.$route?.meta?.hideVisionAi;
    },
    routeMetaHidePrimaryNav() {
      return this.$route?.meta?.hidePrimaryNav;
    },
    showNav() {
      return !this.routeMetaReport && !this.routeMetaHideNav && !!this.authStore.identity?.id;
    },
    showVisionAi() {
      if (this.visionAiStore.forceHideVisionAi) return false;
      if (!this.authStore.identity?.id) return false;

      const { userCanAccessVisionAiForSomeBrandOrOrg } = useVisionAiPermissions();
      return (
        !this.routeSalesforceEmbed &&
        !this.routeMetaReport &&
        !this.routeMetaShared &&
        !this.routeMetaHideVisionAi &&
        userCanAccessVisionAiForSomeBrandOrOrg.value
      );
    },
    routeMetaLayout() {
      return this.$route?.meta?.layout;
    },
    routeName() {
      return this.$route?.name;
    },
    hideBillingAndPlans() {
      return this.flagStore.ready && this.flagStore.flags.hideBillingAndPlans;
    },
    showHubspotChat() {
      return !this.hideBillingAndPlans && !this.routeSalesforceEmbed && !this.routeMetaShared;
    },
    showCheckTimezoneDialog() {
      return !this.routeMetaShared;
    },
  },
  watch: {
    hideBillingAndPlans: {
      handler(value) {
        if (value) {
          this.setHideBillingCookie();
        }
      },
      immediate: true,
    },
    'authStore.currentBrand': {
      handler() {
        this.sharedStore.resetBrandState();
        this.handleBrandChange();
      },
    },
    'authStore.identity': {
      handler(newVal, oldVal) {
        this.handleBrandChange();
        this.loadTwitterAccounts();

        if (newVal && !oldVal) {
          this.fetchMetricDetails();
        }
      },
    },
    $route: {
      handler(to) {
        if (to && to.meta && to.meta.report) {
          // move it from beforeMount to here as beforeMount and even a little while after mounted,
          // $route is empty.
          this.loadReportStyle();
        }
      },
      immediate: true,
    },
  },
  created() {
    // Track start of session in Mixpanel
    window.addEventListener('mousemove', (e) => {
      this.currentCursorPos = [e.pageX, e.pageY];
    });
    this.trackSession();
    setInterval(this.trackSession, sessionInterval);

    if (!this.routeMetaReport) {
      this.fullstoryStore.init();
      this.datadogStore.init();
    }

    // Prevent users from continuing if they haven't completed their account setup
    if (this.accountSetupNotComplete()) {
      this.$router.replace({ name: 'account.setup' });
    } else if (this.$route.name !== 'account.setup') {
      const unwatchIdentity = this.$watch(
        () => this.authStore.identity,
        () => {
          if (this.accountSetupNotComplete()) {
            this.$router.replace({ name: 'account.setup' });
          }
          unwatchIdentity();
        },
      );
    }
  },
  mounted() {
    // Preload Lottie module early to reduce animation startup delay
    preloadLottie();
    preloadImages([noAccessBg, dashSocialLogoPrimaryBlack, dashSocialIconPrimary]);

    // Timeout fallback: if the Lottie animation fails to load from CDN,
    // @on-complete never fires and the user gets stuck. Clear after 6s
    // (animation is ~5s at 136 frames / 27fps).
    if (this.authStore.shouldShowLoginAnimation) {
      this.loginAnimationTimeout = setTimeout(() => this.clearLoginAnimation(), 6000);
    }

    // This disconnects emails accounts from creators if they don't have access to creators
    this.disconnectInaccessibleEmailAccounts();

    this.handleBrandChange();
    this.loadTwitterAccounts();
    this.setupAppEventListeners();
    if (!this.routeMetaShared) {
      this.platformStore.init();
    }
    this.fetchMetricDetails();
    this.getCompetitorList();
  },
  methods: {
    clearLoginAnimation() {
      clearTimeout(this.loginAnimationTimeout);
      this.authStore.clearLoginAnimation();
    },
    // for hiding billing and plans
    setHideBillingCookie() {
      const hostname = window.location.hostname;
      if (!hostname.endsWith('dashsocial.com')) return;
      document.cookie = `HIDE_BILLING=True; domain=.dashsocial.com; path=/; SameSite=Lax; Secure`;
    },
    disconnectInaccessibleEmailAccounts() {
      setTimeout(async () => {
        const brands = Object.values(this.authStore.identity?.brands ?? {}).filter(
          (brand) => brand.is_brand !== false,
        );
        if (brands.length === 0) return;
        const canUseCreatorEmail = brands.some((brand) =>
          this.authStore.guard(BRAND.CREATORS.CAN_ACCESS_CREATORS, brand),
        );
        if (
          !canUseCreatorEmail &&
          !this.integrationStore.authenticating &&
          !this.integrationStore.isAuthenticatedAsBrand
        ) {
          await this.integrationStore.authenticate(AUTHENTICATION_TYPE.user);
          const connectedEmailProviders = EMAIL_PROVIDERS.filter((provider) =>
            this.integrationStore.isConnected(provider),
          );
          if (connectedEmailProviders.length > 0) {
            await connectedEmailProviders.reduce(
              (promise, provider) => promise.then(this.integrationStore.disconnect(provider)),
              Promise.resolve(),
            );
            this.notificationStore.setToast({
              message:
                'Your email connection for Creator Management has been disconnected. ' +
                'New emails will no longer be streamed, and previous emails are not stored ' +
                'in Dash Social.',
              type: 'primary',
              autoClear: false,
            });
          }
        }
      }, 2000);
    },
    fetchMetricDetails() {
      if (this.authStore.isLoggedIn) {
        this.metricsStore.fetchMetricDetails();
      }
    },
    getCompetitorList() {
      if (this.authStore.isLoggedIn) {
        // load competitors into store. Used for dashboards UI and global Vision AI chat
        const competitiveChannelTypes = Object.values(CHANNELS)
          .filter(filterNonCompetitiveChannels())
          .map((channel) => channel.competitiveChannelType);
        return Promise.all(
          competitiveChannelTypes.map((channelType) =>
            this.searchStore.getCompetitors(
              channelType,
              this.dashboardsStore.dashboardIdentityBrandsByIds,
            ),
          ),
        );
      }
      return Promise.resolve();
    },
    handleBrandChange() {
      if (
        this.authStore.identity &&
        this.authStore.currentBrand &&
        this.authStore.currentBrand.label
      ) {
        this.visionAiStore.closeChatWindow();
        this.visionAiStore.startNewChat();
        this.authStore.clearBrandAccessibleUsers();
        if (this.authStore.isLoggedIn && !this.routeMetaShared) {
          this.schedulerStore.getFacebookSchedulingToken();
        }
        if (this.authStore.currentBrand.permissions?.instagram?.can_access_instagram) {
          this.instagramAccountStore.getBrandInstagramAccount();
        }

        if (this.routeMetaReport || this.routeName === null) {
          return;
        }
        this.chiliPiperStore.init();
        this.intercomStore.boot();
        this.hubspotStore.boot();
        this.survicateStore.init();
        this.datadogStore.setupUser();
        if (this.authStore.isLoggedIn && !this.routeMetaShared) {
          this.authStore.getBrandAccessibleUsers({ brandId: this.authStore.currentBrand?.id });
        }
        this.loadTwitterAccounts();
        this.getCompetitorList();
      }
    },
    trackSession() {
      let sessionExpired = true;

      const lastCheck = browserStorageGetItem(trackSessionKey);
      const timestamp = Date.now();

      if (lastCheck && timestamp - lastCheck < sessionInterval) {
        sessionExpired = false;
      } else {
        browserStorageSetItem(trackSessionKey, timestamp.toString());
      }

      if (sessionExpired && !isEqual(this.currentCursorPos, this.savedCursorPos)) {
        this.savedCursorPos = this.currentCursorPos;
        this.trackingStore.track(`Session`);
      }
    },
    loadReportStyle() {
      document.body.style.background = 'white';
      if (this.routeMetaLayout === 'horizontal') {
        setCssPageMedia(`
          size: 1150px 735px;
          margin: 0;
        `);
      }
    },
    handleDragEnter(event) {
      // make whole app responsive to drag and drop upload
      event.preventDefault();
      // ignore other draggable elements.
      if (event.dataTransfer.items.length === 0 || event.dataTransfer.items[0].kind !== 'file') {
        return;
      }
      // disable drag & drop when popup is open.
      if (this.hasOpenPopup) {
        return;
      }
      if (this.$route.name !== 'auth.login') {
        // TODO: open upload popup here...
      }
    },
    handleDragLeave(event) {
      // dragleave event fires whenever drag leaves current DOM node.
      // (i.e, hovering on different Node, or moved out of window)
      // we only resume page when mouse moved out of the window.
      event.preventDefault();
      if (
        event.clientX === 0 &&
        event.clientY === 0 &&
        this.$route.name === 'library.media.upload'
      ) {
        const existingFileLength = this.$refs.uploadPopup.mediaItems.length;
        if (!existingFileLength) {
          this.$router.back();
        }
      }
    },
    setupAppEventListeners() {
      window.addEventListener('dragenter', this.handleDragEnter.bind(this));
      window.addEventListener('dragleave', this.handleDragLeave.bind(this));

      // This will disable default browser drag/drop functionality
      // (prevents drag/drop outside dropzone)
      document.addEventListener('dragover', (e) => e.preventDefault());
      document.addEventListener('drop', (e) => e.preventDefault());
    },
    accountSetupNotComplete() {
      return !!(
        this.authStore.identity &&
        // Brands are added to identity for anonymous users as part of the platform reconnection process
        this.authStore.identity.id &&
        this.authStore.identity.invitation_status !== 'completed' &&
        this.$route.name !== 'account.setup'
      );
    },
    loadTwitterAccounts() {
      if (this.authStore.isLoggedIn && !this.routeMetaShared) {
        const identityBrandIds = Object.keys(this.authStore.identity.brands).map(
          (brandLabel) => this.authStore.identity.brands[brandLabel].id,
        );
        const brandIds = identityBrandIds;
        this.twitterStore.getTwitterAccounts({ brandIds });
      }
    },
  },
});
export default comp;
</script>

<style lang="postcss">
.app-view {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-touch-callout: none; /* iOS Safari */

  &.report-view {
    background: var(--background-0);
  }
}

@media print {
  body {
    background: var(--background-0) !important;
  }
}

.dashsocial-animation-wrapper {
  position: fixed;
  inset: var(--dash-top-banner-offset, 0) 0 0 0;
  z-index: 50000;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: var(--background-0);
  pointer-events: none;
}

.identity-loading-overlay {
  position: fixed;
  inset: var(--dash-top-banner-offset, 0) 0 0 0;
  z-index: 49999;
  background: var(--background-0);
}

.login-animation-fade-leave-active {
  transition: opacity 300ms ease-out;
}

.login-animation-fade-leave-to {
  opacity: 0;
}
</style>
